import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorInfo, PostSectionsPsdiffcheckBody } from '../../../../../api/model';
import { usePostSectionsPsdiffcheck } from '../../../../../api/sections/sections';
import { usePostUtilsDownload } from '../../../../../api/utils/utils';
import { useDefaultOptions, useOptionsForBinary } from '../../../../../hooks/api';
import '../../../../../styles/Dropdown.css';
import { executeDownload } from '../../../../../utils/download';
import BlobErrorModal from '../../../../modules/BlobErrorModal';
import ConfirmModal from '../../../../modules/ConfirmModal';
import ErrorModal from '../../../../modules/ErrorModal';
import Modal from '../../../../modules/Modal';
import SpinnerModal from '../../../../modules/SpinnerModal';

type Props = {
  open: boolean;
  onClose: () => void;
  sectionId: number;
};
function PsDiffPModal({ open, onClose, sectionId }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // フォーム要素
  const { register, handleSubmit } = useForm<PostSectionsPsdiffcheckBody>({
    defaultValues: {
      diff_type: 1,
      output_type: '1',
      language_id: '0',
      legal_region_ids: undefined,
    },
  });

  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false); // スピナーモーダルを表示する
  const [displayPsDiffError, setDisplayPsDiffError] = useState(false); // エラー
  const [displayConfirmPsDiffRun, setDisplayConfirmPsDiffRun] = useState(false); // 確認
  const [displayDownLoadError, setDisplayDownLoadError] = useState(false); // ダウンロードエラー

  const options = useDefaultOptions(language);
  const optionsForBinary = useOptionsForBinary(language);

  // PS差異API
  const {
    mutateAsync: _pdDiffAsync,
    isError: psDiffIsError,
    error: psDiffError,
  } = usePostSectionsPsdiffcheck<AxiosError<ErrorInfo>, unknown>(options);
  useEffect(() => {
    setDisplayPsDiffError(psDiffIsError);
  }, [psDiffIsError]);

  // ダウンロードAPI
  const {
    mutateAsync: _downloadAsync,
    isError: downloadIsError,
    error: downloadError,
  } = usePostUtilsDownload<AxiosError<Blob>>(optionsForBinary);
  useEffect(() => {
    setDisplayDownLoadError(downloadIsError);
  }, [downloadIsError]);

  // PS差異処理
  const onSubmit = useCallback(
    () =>
      handleSubmit(async (data: PostSectionsPsdiffcheckBody) => {
        setDisplaySpinnerModal(true);
        if (sectionId != null) {
          const responseDiff = await _pdDiffAsync({
            sectionId,
            data,
          });
          if (responseDiff.status === 200) {
            // 処理が成功したら結果をダウンロード
            if (responseDiff.data.result_file_info !== undefined && responseDiff.data.result_file_info !== null) {
              await _downloadAsync(
                { data: responseDiff.data.result_file_info },
                {
                  onSuccess: (responseDL) => {
                    executeDownload(responseDL);
                  },
                }
              );
            }
            // 画面に表示
            if (data.output_type === '10') {
              // 属性値を加えることで原稿編集保存処理にどのような影響を与えるのか現在検証をしていないので、この機能はとりあえずコメントアウト
              // ハイライトを消す
              document.querySelectorAll('div[ps_diff_id]').forEach((element: Element) => {
                element.removeAttribute('ps_diff_id');
              });

              if (responseDiff.data.result_psdiff_data !== undefined) {
                responseDiff.data.result_psdiff_data.forEach((value) => {
                  if (value.ps_diff_id !== undefined && value.ps_no !== undefined) {
                    document.querySelectorAll(`div[ps_no=${value.ps_no}]`).forEach((element: Element) => {
                      element.setAttribute('ps_diff_id', String(value.ps_diff_id));
                    });
                  }
                });
              }
            }
            // ダイアログを閉じる
            void onClose();
          }
        }
        setDisplaySpinnerModal(false);
      })(),
    [sectionId, _pdDiffAsync, _downloadAsync, onClose, handleSubmit]
  );

  return (
    <>
      <Modal active={open} additionalClassName="popupfr400" displayCloseButton onClose={onClose}>
        <form>
          <div className="popupfr400 popuppsdiff">
            <div className="title">
              <label>{t('PS差異確認')}</label>
            </div>
            <div className="selecter">
              <div className="labip5">
                <div className="mb5">
                  <label>{t('言語')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={0} {...register('language_id')} id="lang_ja" />
                  <label htmlFor="lang_ja">{t('日本語')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={1} {...register('language_id')} id="lang_us" />
                  <label htmlFor="lang_us">{t('米語')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={2} {...register('language_id')} id="lang_en" />
                  <label htmlFor="lang_en">{t('英語')}</label>
                </div>
              </div>
              <div className="labip5">
                <div className="mb5">
                  <label>{t('出力単位')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={10} {...register('output_type')} id="sec_d" />
                  <label htmlFor="sec_d">{t('L2文書(Display)')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={3} {...register('output_type')} id="sec" />
                  <label htmlFor="sec">{t('L2文書')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={2} {...register('output_type')} id="chap" />
                  <label htmlFor="chap">{t('章全体')}</label>
                </div>
                <div>
                  <input className="radionr" type="radio" value={1} {...register('output_type')} id="prod" />
                  <label htmlFor="prod">{t('文書全体')}</label>
                </div>
              </div>
            </div>
            <div className="btndiv">
              <div>
                <span className="btn2 btnbg-g mr10" style={{ width: '100px' }}>
                  <span className="modal-close2" onClick={onClose}>
                    {t('キャンセル').toString()}
                  </span>
                </span>
                <span>
                  <input
                    type="button"
                    value={t('出力').toString()}
                    className="btn1 lsp1"
                    onClick={() => setDisplayConfirmPsDiffRun(true)}
                    style={{ width: '100px' }}
                  />
                </span>
              </div>
            </div>
          </div>
        </form>
      </Modal>
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* エラー */}
      {displayPsDiffError && (
        <ErrorModal
          error={psDiffError}
          onClickOk={() => {
            setDisplayPsDiffError(false);
            setDisplaySpinnerModal(false);
          }}
        />
      )}
      {/* 確認 */}
      {displayConfirmPsDiffRun && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmPsDiffRun(false)}
          onClickOk={async () => {
            setDisplayConfirmPsDiffRun(false);
            await onSubmit();
          }}
        >
          {t('PS差異チェックを実行しますか？')}
        </ConfirmModal>
      )}
      {/* エラー */}
      {displayDownLoadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownLoadError(false)} />
      )}
    </>
  );
}

export { PsDiffPModal };
